import type { AttributeRule } from 'types';
import type { RuleDraft } from './attribute-rules';
import { useState } from 'react';
import { EMPTY_RULE_DRAFT, MAX_ATTRIBUTE_RULES, parseRuleDraft, valueKindsFor } from './attribute-rules';

type UseRuleDraftParams = {
  rules: readonly AttributeRule[];
  /** Keeps the new rule; the dialog closes once this resolves true. */
  onAdd: (rule: AttributeRule) => Promise<boolean> | boolean;
};

/**
 * The Add attribute rule dialog's state: the draft, whether it parses, and adding it. Shared by a
 * Server-side experiment's rules (saved at once) and a feature flag's rings (kept in the ring's
 * draft until Save), so the dialog behaves the same in both.
 */
export const useRuleDraft = ({ rules, onAdd }: UseRuleDraftParams) => {
  const [ruleDraft, setRuleDraft] = useState<RuleDraft | null>(null);
  const parsedRule = ruleDraft ? parseRuleDraft(ruleDraft) : null;
  const rulesFull = rules.length >= MAX_ATTRIBUTE_RULES;

  const openRuleDialog = () => !rulesFull && setRuleDraft(EMPTY_RULE_DRAFT);
  const closeRuleDialog = () => setRuleDraft(null);

  /** Changing the operator resets the value kind to one the new operator takes. */
  const updateRuleDraft = (patch: Partial<RuleDraft>) =>
    setRuleDraft((current) => {
      if (!current) return current;
      const next = { ...current, ...patch };
      const kinds = valueKindsFor(next.operator);
      return kinds.includes(next.valueKind) ? next : { ...next, valueKind: kinds[0] ?? 'text' };
    });

  const addRule = async () => {
    if (!parsedRule?.ok) return;
    if (await onAdd(parsedRule.rule)) closeRuleDialog();
  };

  return { ruleDraft, parsedRule, rulesFull, openRuleDialog, closeRuleDialog, updateRuleDraft, addRule };
};

export type RuleDraftManager = ReturnType<typeof useRuleDraft>;

/** What the rules editor reads: the rules, removing one, and the dialog. */
export type AttributeRulesManager = RuleDraftManager & {
  rules: readonly AttributeRule[];
  isSaving: boolean;
  removeRule: (index: number) => void | Promise<void>;
};
